import React, { useRef, useState } from "react";
import { useNotification } from "../../context/NotificationContext";
import { motion } from "framer-motion";
import { Calendar, ShieldCheck, ShieldAlert, LogOut, Shield, Camera, Key } from "lucide-react";
import api from "../../api";
import { Button } from "./button";
import { Badge } from "./Badge";

const getImageUrl = (path) => {
  if (!path) return '/assets/logo-hipparchus.png';
  if (path.startsWith('http') || path.startsWith('data:image')) return path;
  return path.startsWith('/') ? path : `/${path}`;
};

export default function Profile({ user, onLogout }) {
  const { showToast } = useNotification();
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleAvatarClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    const data = new FormData();
    data.append("avatar", file);

    try {
      await api.put("/utilisateurs/me/avatar", data);
      showToast("Photo de profil mise à jour", "success");
      window.location.reload();
    } catch (err) {
      showToast("Erreur lors de la mise à jour", "error");
    } finally {
      setIsUploading(false);
    }
  };

  const handleLogoutClick = () => {
    showToast("Déconnexion en cours...", "info");
    if (onLogout) onLogout();
  };

  const formattedDate = user?.createdAt
    ? new Date(user.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
    : "Non renseigné";

  return (
    <div className="w-full p-4 sm:p-5">
      {/* En-tête profil compact */}
      <div className="flex flex-col items-center text-center pt-1">
        {/* Avatar avec déclencheur photo */}
        <div className="relative group cursor-pointer" onClick={handleAvatarClick} title="Changer la photo de profil">
          <motion.img
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.2 }}
            className={`h-16 w-16 rounded-full object-cover border-2 border-workspace shadow-hp-sm transition duration-200 group-hover:brightness-90 ${isUploading ? 'opacity-50 animate-pulse' : ''}`}
            src={getImageUrl(user?.profileImage)}
            alt="Profil utilisateur"
          />
          {/* Badge caméra discret en bas à droite */}
          <div className="absolute -bottom-1 -right-1 p-1 bg-surface rounded-full border border-workspace shadow-sm text-subtle group-hover:text-primary group-hover:scale-110 transition">
            <Camera className="w-3 h-3" />
          </div>
          <input 
            type="file" 
            ref={fileInputRef} 
            className="hidden" 
            accept="image/png, image/jpeg, image/webp" 
            onChange={handleFileChange} 
          />
        </div>

        {/* Nom & Email */}
        <div className="mt-3 w-full px-2">
          <h2 className="text-sm font-bold text-primary truncate" title={user?.Nom || "Utilisateur"}>
            {user?.Nom || "Utilisateur"}
          </h2>
          <p className="text-xs text-subtle truncate mt-0.5" title={user?.Email || "Non renseigné"}>
            {user?.Email || "Non renseigné"}
          </p>
        </div>

        {/* Badge de Rôle */}
        <div className="mt-2.5">
          <Badge variant="secondary" className="border border-workspace/70 text-[11px] py-0.5 px-2.5 gap-1.5 font-medium">
            <Shield className="w-3 h-3 text-info-500 shrink-0" />
            {user?.jobTitle || user?.Rôle || user?.roleNames?.[0] || "Utilisateur"}
          </Badge>
        </div>
      </div>

      {/* Ligne de séparation */}
      <div className="my-3.5 border-t border-workspace/60" />

      {/* Informations structurées compactes */}
      <div className="space-y-2">
        {/* Double authentification 2FA */}
        <div className="flex items-center justify-between p-2 rounded-lg bg-surface-muted/60 border border-workspace/40 text-xs">
          <div className="flex items-center gap-2 text-muted font-medium">
            {user?.isTwoFactorEnabled ? (
              <ShieldCheck className="w-3.5 h-3.5 text-success-500 shrink-0" />
            ) : (
              <ShieldAlert className="w-3.5 h-3.5 text-amber-500 shrink-0" />
            )}
            <span>Sécurité 2FA</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`w-1.5 h-1.5 rounded-full ${user?.isTwoFactorEnabled ? 'bg-success-500 animate-pulse' : 'bg-amber-500'}`} />
            <span className={`text-[11px] font-semibold ${user?.isTwoFactorEnabled ? 'text-success-600 dark:text-success-400' : 'text-amber-600 dark:text-amber-400'}`}>
              {user?.isTwoFactorEnabled ? "Active" : "Inactive"}
            </span>
          </div>
        </div>

        {/* Date d'inscription */}
        <div className="flex items-center justify-between p-2 rounded-lg bg-surface-muted/60 border border-workspace/40 text-xs">
          <div className="flex items-center gap-2 text-muted font-medium">
            <Calendar className="w-3.5 h-3.5 text-subtle shrink-0" />
            <span>Membre depuis</span>
          </div>
          <span className="text-[11px] font-medium text-primary">
            {formattedDate}
          </span>
        </div>

        {/* Permissions */}
        <div className="p-2 rounded-lg bg-surface-muted/60 border border-workspace/40 text-xs">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2 text-muted font-medium">
              <Key className="w-3.5 h-3.5 text-subtle shrink-0" />
              <span>Permissions</span>
            </div>
            {user?.permissions && user.permissions.length > 0 && (
              <span className="text-[10px] text-subtle font-mono">
                {user.permissions.length} accordée{user.permissions.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1">
            {user?.permissions && user.permissions.length > 0 ? (
              <>
                {user.permissions.slice(0, 3).map((perm, idx) => (
                  <span 
                    key={idx} 
                    className="inline-block bg-surface border border-workspace text-[10px] font-medium text-muted px-1.5 py-0.5 rounded shadow-hp-xs truncate max-w-[110px]"
                    title={perm}
                  >
                    {perm}
                  </span>
                ))}
                {user.permissions.length > 3 && (
                  <span className="inline-block bg-surface border border-workspace text-[10px] font-semibold text-subtle px-1.5 py-0.5 rounded shadow-hp-xs">
                    +{user.permissions.length - 3}
                  </span>
                )}
              </>
            ) : (
              <span className="text-[11px] text-subtle italic">
                Rôle standard (@everyone)
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Ligne de séparation */}
      <div className="my-3.5 border-t border-workspace/60" />

      {/* Action Déconnexion */}
      <Button
        variant="outline"
        size="sm"
        className="w-full justify-center gap-2 text-danger-600 dark:text-danger-400 border-danger-200 dark:border-danger-900/40 hover:bg-danger-50 dark:hover:bg-danger-950/30 hover:text-danger-700 dark:hover:text-danger-300 transition-colors py-2 text-xs font-semibold rounded-xl shadow-none"
        onClick={handleLogoutClick}
      >
        <LogOut className="w-3.5 h-3.5" />
        Se déconnecter
      </Button>

      {/* Version */}
      <p className="mt-3 text-[10px] text-subtle text-center font-mono opacity-60">
        Hipparchus • Beta 0.0.2
      </p>
    </div>
  );
}
